import React from 'react';
import styled from 'styled-components';
import { Badge, Button, Text } from '@teals/ui-web';
import type { AgentDetail, AgentFleet, AgentMembership, Agent } from '../../store/api/agentsApi';

const SummaryCard = styled.div`
  border: 1px solid color-mix(in srgb, var(--accent) 30%, var(--border));
  border-radius: 0.625rem;
  padding: 1rem;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SummaryTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SummaryBody = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const FleetHeading = styled.h4`
  margin: 0 0 0.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const DisclosureBtn = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  margin-top: 0.5rem;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const FleetDiagram = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--bg-elevated, #f8fafc);
`;

const DiagramMgr = styled.div`
  text-align: center;
  margin-bottom: 0.75rem;

  span {
    display: inline-block;
    padding: 0.5rem 1rem;
    border: 2px solid var(--accent);
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    background: var(--bg-surface);
  }
`;

const DiagramEdges = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

const DiagramSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;

  span {
    padding: 0.375rem 0.625rem;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 0.6875rem;
    background: var(--bg-surface);
    color: var(--text-secondary);
  }
`;

const FleetList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
`;

const FleetItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  cursor: pointer;
  color: inherit;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-elevated, #f8fafc);
  }
`;

const FleetName = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const FleetRole = styled.span`
  flex: 1;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  background: var(--bg-elevated, #f1f5f9);
  color: var(--text-secondary);
`;

const HintChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }
`;

const MembershipLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  color: var(--text-primary);
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const ConfigBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  background: var(--bg-surface);
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
`;

export function typeBadgeLabel(agentType?: string): string {
  const t = (agentType || 'conversational').toLowerCase();
  if (t === 'management') return 'Management';
  if (t === 'task') return 'Task';
  return 'Conversational';
}

export function isManagementAgent(agent?: AgentDetail | null): boolean {
  if (!agent) return false;
  if ((agent.agentType || '').toLowerCase() === 'management') return true;
  return Boolean(agent.fleet?.agents?.length);
}

export const HintChips: React.FC<{
  agent: AgentDetail;
  onOpenPlayground: () => void;
  onAssociate?: () => void;
  onConfigure?: () => void;
}> = ({ agent, onOpenPlayground, onAssociate, onConfigure }) => {
  const management = isManagementAgent(agent);
  const needsApp =
    agent.versionStatus === 'published' &&
    (agent.applicationCount ?? agent.applications?.length ?? 0) === 0;

  return (
    <ChipRow>
      {management && (
        <HintChip type="button" onClick={onOpenPlayground}>
          Test this system in Playground
        </HintChip>
      )}
      {needsApp && (
        <HintChip type="button" onClick={onAssociate}>
          Associate with an application?
        </HintChip>
      )}
      {onConfigure && (
        <HintChip type="button" onClick={onConfigure}>
          Configure models & sources
        </HintChip>
      )}
    </ChipRow>
  );
};

export const MembershipChip: React.FC<{
  membership: AgentMembership;
  onOpenParent: (idOrName: string) => void;
}> = ({ membership, onOpenParent }) => (
  <MembershipLink
    type="button"
    onClick={() =>
      onOpenParent(membership.parentId || membership.parentAgentName)
    }
  >
    Part of · {membership.parentAgentName}
  </MembershipLink>
);

export const CapabilitiesRow: React.FC<{ capabilities?: string[] }> = ({
  capabilities,
}) => {
  if (!capabilities?.length) return null;
  return (
    <ChipRow>
      {capabilities.map((c) => (
        <Chip key={c}>{c}</Chip>
      ))}
    </ChipRow>
  );
};

export function fleetMemberLabel(member: {
  agentName: string;
  displayName?: string;
}): string {
  const raw = (member.displayName || member.agentName || '').trim();
  if (!raw) return member.agentName;
  if (raw.includes(' ') || raw !== member.agentName) return raw;
  const parts = raw.split(/[-_]/).filter(Boolean);
  const last = parts[parts.length - 1] || raw;
  return last.charAt(0).toUpperCase() + last.slice(1);
}

export const AgentSystemSummary: React.FC<{
  fleet: AgentFleet;
  onOpenAgent: (idOrName: string) => void;
  managerLabel?: string;
}> = ({ fleet, onOpenAgent, managerLabel }) => {
  const [showDiagram, setShowDiagram] = React.useState(false);
  const count = fleet.agents.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <SummaryCard>
        <SummaryTitle>Agent system</SummaryTitle>
        <SummaryBody>
          {count} specialist{count === 1 ? '' : 's'}
          {fleet.orchestrationLabel ? ` · ${fleet.orchestrationLabel}` : ''}.
          Prefer associating <strong>this</strong> agent with applications.
        </SummaryBody>
      </SummaryCard>
      {count > 0 && (
        <>
          <div>
            <FleetHeading>Fleet</FleetHeading>
            <FleetList>
              {fleet.agents.map((m) => {
                const label = fleetMemberLabel(m);
                const role =
                  m.role && m.role !== m.agentName && m.role !== label
                    ? m.role
                    : undefined;
                return (
                  <FleetItem
                    key={m.agentName}
                    type="button"
                    onClick={() => onOpenAgent(m.id || m.agentName)}
                  >
                    <FleetName>{label}</FleetName>
                    <FleetRole>{role || ''}</FleetRole>
                    {m.agentType && (
                      <Badge variant="info" size="sm">
                        {typeBadgeLabel(m.agentType)}
                      </Badge>
                    )}
                  </FleetItem>
                );
              })}
            </FleetList>
          </div>
          <DisclosureBtn
            type="button"
            onClick={() => setShowDiagram((v) => !v)}
          >
            {showDiagram
              ? 'Hide fan-out diagram ▴'
              : 'Show simple fan-out diagram ▾'}
          </DisclosureBtn>
          {showDiagram && (
            <FleetDiagram>
              <DiagramMgr>
                <span>{managerLabel || 'Management agent'}</span>
              </DiagramMgr>
              <DiagramEdges>↓ fan-out (parallel) ↓</DiagramEdges>
              <DiagramSpecs>
                {fleet.agents.map((m) => (
                  <span key={m.agentName}>{fleetMemberLabel(m)}</span>
                ))}
              </DiagramSpecs>
            </FleetDiagram>
          )}
        </>
      )}
    </div>
  );
};

export const PartOfSystemCard: React.FC<{
  membership: AgentMembership;
  onOpenParent: (idOrName: string) => void;
}> = ({ membership, onOpenParent }) => (
  <SummaryCard>
    <SummaryTitle>Part of system</SummaryTitle>
    <MembershipChip membership={membership} onOpenParent={onOpenParent} />
    {membership.role && (
      <Text variant="body1" style={{ margin: 0, color: 'var(--text-secondary)' }}>
        Role: {membership.role}
      </Text>
    )}
    <Text variant="body1" style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
      Prefer associating the parent system for production integrations.
    </Text>
  </SummaryCard>
);

export const ConfigureFleetPanel: React.FC<{
  fleet: AgentFleet | null | undefined;
  catalog: Agent[];
  readOnly: boolean;
  onChange: (agentNames: string[]) => void;
  onOpenAgent: (idOrName: string) => void;
}> = ({ fleet, catalog, readOnly, onChange, onOpenAgent }) => {
  const members = fleet?.agents || [];
  const memberNames = new Set(members.map((m) => m.agentName));
  const addable = catalog.filter(
    (a) =>
      a.agentName &&
      !memberNames.has(a.agentName) &&
      (a.agentType || '').toLowerCase() !== 'management',
  );

  const removeMember = (name: string) => {
    if (!window.confirm(`Remove ${name} from this fleet?`)) return;
    onChange(members.map((m) => m.agentName).filter((n) => n !== name));
  };

  const addMember = (name: string) => {
    if (!name) return;
    onChange([...members.map((m) => m.agentName), name]);
  };

  return (
    <ConfigBlock>
      <SummaryTitle>Fleet & orchestration</SummaryTitle>
      <Text variant="body1" style={{ margin: 0, color: 'var(--text-secondary)' }}>
        {fleet?.orchestrationLabel || 'Configure specialist agents for this system.'}
        {fleet?.orchestrationMode ? ` (${fleet.orchestrationMode})` : ''}
      </Text>
      {members.length === 0 && (
        <Text variant="body1" style={{ margin: 0, color: 'var(--danger, #dc2626)' }}>
          Empty fleet is warned on Publish.
        </Text>
      )}
      {members.map((m) => (
        <MemberRow key={m.agentName}>
          <button
            type="button"
            onClick={() => onOpenAgent(m.id || m.agentName)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              color: 'var(--accent)',
              fontWeight: 600,
            }}
          >
            {m.displayName || m.agentName}
          </button>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={() => removeMember(m.agentName)}>
              Remove
            </Button>
          )}
        </MemberRow>
      ))}
      {!readOnly && (
        <select
          aria-label="Add fleet member"
          defaultValue=""
          onChange={(e) => {
            addMember(e.target.value);
            e.target.value = '';
          }}
          style={{
            minHeight: 44,
            border: '2px solid var(--border)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">+ Add fleet member</option>
          {addable.map((a) => (
            <option key={a.id} value={a.agentName}>
              {a.name}
            </option>
          ))}
        </select>
      )}
    </ConfigBlock>
  );
};

export const ConfigureMembershipPanel: React.FC<{
  membership: AgentMembership;
  onOpenParent: (idOrName: string) => void;
}> = ({ membership, onOpenParent }) => (
  <ConfigBlock>
    <SummaryTitle>System membership</SummaryTitle>
    <MembershipChip membership={membership} onOpenParent={onOpenParent} />
    {membership.role && (
      <Text variant="body1" style={{ margin: 0 }}>
        Role: {membership.role}
      </Text>
    )}
    <Text variant="body1" style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
      Playground and Sessions still work on this agent alone.
    </Text>
  </ConfigBlock>
);
